import type { WCollectionGroupView } from '@zola/svc-web-api-ts-client';
import { receiveUser } from '@zola-helpers/client/dist/es/redux/user/userActions';
import { UserContext } from '@zola-helpers/client/dist/types/@types';

import { GetServerSideProps } from 'next';
import NextHead from 'next/head';
import type { CamelCasedPropertiesDeep } from 'type-fest';

import { serverIsBaby } from 'server/lib/next/isBaby';
import { RegistryShoppingPage } from '~/contexts/shop/RegistryShoppingPage';
import {
  getCollectionGroupForPlatform,
  getCollectionGroupView,
  getDefaultCollectionGroup,
} from '~/lib/server/api/collectionGroup';
import { getUserContext } from '~/lib/server/api/user';
import { GiftCollections } from '~/pages/GiftCollections/GiftCollections';
import { initializeStore } from '~/store/nextStore';

type GiftCollectionPageProps = {
  isBaby: boolean;
  userContext: UserContext | null;
  collectionGroup: CamelCasedPropertiesDeep<WCollectionGroupView>;
  collectionGroups: CamelCasedPropertiesDeep<WCollectionGroupView>[];
};

const Helmet = ({
  isBaby,
  collectionGroup,
}: {
  isBaby: boolean;
  collectionGroup: CamelCasedPropertiesDeep<WCollectionGroupView>;
}) => {
  const firstTile = collectionGroup.tileViews?.[0];
  let ogImage = firstTile?.coverImagePath;
  if (ogImage?.startsWith('//')) {
    ogImage = 'https:' + ogImage;
  }

  const baseUrl = isBaby ? 'https://baby.zola.com' : 'https://www.zola.com';
  const url = `${baseUrl}/shop/gift-collection/${collectionGroup.key}`;

  const title = `Starter Collections | ${collectionGroup.name} | Zola`;
  return (
    <NextHead>
      <title key="title">{title}</title>
      <meta name="description" content={collectionGroup.description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={collectionGroup.description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Zola" />
      <meta property="og:url" content={url} />
      <meta name="twitter:site" content="@zola" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:image" content={ogImage} />
    </NextHead>
  );
};

export const GiftCollectionPage = ({
  isBaby,
  userContext,
  collectionGroup,
  collectionGroups,
}: GiftCollectionPageProps) => {
  return (
    <RegistryShoppingPage isBaby={isBaby} userContext={userContext}>
      <Helmet collectionGroup={collectionGroup} isBaby={isBaby} />
      <GiftCollections
        collectionGroup={collectionGroup}
        collectionGroups={collectionGroups}
        isBaby={isBaby}
        userContext={userContext}
      />
    </RegistryShoppingPage>
  );
};

export const getServerSideProps: GetServerSideProps<GiftCollectionPageProps> = async (context) => {
  const isBabyPage = serverIsBaby(context);

  const reduxStore = initializeStore();
  const { dispatch, getState } = reduxStore;

  const { collectionKey = null } = context.params || {};

  const groupKey = collectionKey?.[0] as string;

  // Uncaught errors here render the 500 page
  const [userContext, collectionGroup, collectionGroups] = await Promise.all([
    getUserContext(context.req),
    groupKey
      ? getCollectionGroupView(groupKey)
      : getDefaultCollectionGroup(isBabyPage ? 'BABY_REGISTRY' : 'WEDDING_REGISTRY'),
    getCollectionGroupForPlatform(isBabyPage ? 'BABY_REGISTRY' : 'WEDDING_REGISTRY'),
  ]);

  if (userContext) {
    dispatch(receiveUser(userContext));
  }

  const initialReduxState = JSON.stringify(getState());

  if (!collectionGroup) {
    return {
      notFound: true,
    };
  }

  if (!collectionGroups?.find((group) => group.key === collectionGroup?.key)) {
    const firstGroupKey = collectionGroups?.[0]?.key;
    if (firstGroupKey) {
      return {
        redirect: {
          destination: `/shop/gift-collections/${firstGroupKey}`,
          permanent: false,
        },
      };
    }
    return {
      notFound: true,
    };
  }

  return {
    props: {
      initialReduxState,
      userContext,
      isBaby: isBabyPage,
      collectionGroup,
      collectionGroups: collectionGroups || [],
    },
  };
};
export default GiftCollectionPage;
